import { getBudgetAmountAt } from '../../utils/budgetAmounts';
import React, { useState, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import { useCalendarToggles, usePreference } from '../../hooks/usePreference';
import {
  Box,
  ButtonBase,
  Typography,
  Paper,
  Card,
  CardContent,
  IconButton,
  Button,
  styled,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CategoryChip from '../common/CategoryChip';
import { DialogHeader, DialogSummaryBar } from '../common/AppDialog';
import SharedPageHeader from '../common/PageHeader';
import SegmentedControl from '../common/SegmentedControl';
import InfoBanner from '../common/InfoBanner';
import { alpha, darken } from '@mui/material/styles';
import { generateOccurrences, cadenceLabel } from '../../utils/generateOccurrences';

// Styled components
const CalendarContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  // Added top padding to create space above weekday labels
  paddingTop: theme.spacing(1.2),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
}));

// Budgets-like page header (gradient)

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: theme.spacing(0.3),
  width: '100%',
  // fill the viewport; rows flex via gridTemplateRows set per-month at render
  minHeight: 'max(500px, calc(100vh - 330px))',
  overflow: 'visible',
  [theme.breakpoints.down('sm')]: {
    // dvh tracks the visible viewport as mobile browser chrome collapses;
    // the vh line is the fallback for browsers without dvh support
    minHeight: ['max(420px, calc(100vh - 310px))', 'max(420px, calc(100dvh - 310px))'],
  },
}));

const CalendarDay = styled(Box)(({ theme, isCurrentMonth, isToday, hasEvents }) => ({
  minHeight: 85,
  // allow day to grow to fit wrapped content
  maxHeight: 'none',
  minWidth: 0,
  width: '100%',
  padding: theme.spacing(0.2),
  // today gets a calm tint + border instead of a solid fill, so data stays louder than chrome
  border: isToday ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isToday
    ? alpha(theme.palette.primary.main, 0.06)
    : isCurrentMonth
      ? 'transparent'
      : theme.palette.grey[50],
  opacity: isCurrentMonth ? 1 : 0.55,
  position: 'relative',
  cursor: hasEvents ? 'pointer' : 'default',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    backgroundColor: hasEvents ? theme.palette.action.hover : undefined,
  },
}));

const DayNumber = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(0.2),
  right: theme.spacing(0.2),
  fontSize: 'clamp(0.55rem, 0.8vw, 0.75rem)',
  fontWeight: 600,
  zIndex: 3,
}));

// Calendar grid event chip: a compact chip that may wrap to 2 lines. The
// List View uses ListPill instead (a plain pill that never clips).
const EventChip = styled(Chip, {
  // Keep our custom styling props off the underlying DOM node.
  shouldForwardProp: (prop) =>
    !['categorycolor', 'amount', 'eventtype'].includes(prop),
})(({ theme, categorycolor, amount, eventtype }) => {
  // Same rule as CategoryChip: soft tint of the category's own color for the
  // fill, a dark shade of the same family for text — readable on any pastel.
  let bg, fg;
  try {
    const base = categorycolor || (amount > 0 ? theme.palette.success.main : theme.palette.error.main);
    bg = alpha(base, eventtype === 'budget' ? 0.12 : 0.22);
    fg = darken(base, 0.45);
  } catch {
    bg = theme.palette.grey[100];
    fg = theme.palette.grey[700];
  }
  // dashed = planned (budget), solid fill = actual; see legend
  const border = eventtype === 'budget' ? `1px dashed ${alpha(fg, 0.55)}` : '1px solid transparent';

  return {
    marginTop: theme.spacing(0.05),
    fontSize: 'clamp(0.5rem, 0.7vw, 0.65rem)',
    // allow variable height so text can wrap
    height: 'auto',
    paddingLeft: theme.spacing(0.4),
    paddingRight: theme.spacing(0.4),
    paddingTop: theme.spacing(0.15),
    paddingBottom: theme.spacing(0.15),
    backgroundColor: bg,
    color: fg,
    fontWeight: 600,
    display: 'block',
    alignItems: 'flex-start',
    border,
    '& .MuiChip-label': {
      fontSize: 'inherit',
      lineHeight: 1.05,
      padding: '0 4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
  };
});

// List View category pill. Deliberately NOT a MUI Chip: the Chip's label
// (overflow:hidden over a fixed/centred height) is exactly what kept slicing
// descenders here across two prior fixes. A plain inline-block with real
// vertical padding and a roomy line-height can never clip the text, while
// still ellipsising names that are too long for the row.
const ListPill = styled('span', {
  shouldForwardProp: (prop) =>
    !['categorycolor', 'amount', 'eventtype'].includes(prop),
})(({ theme, categorycolor, amount, eventtype }) => {
  let bg, fg;
  try {
    const base = categorycolor || (amount > 0 ? theme.palette.success.main : theme.palette.error.main);
    bg = alpha(base, eventtype === 'budget' ? 0.12 : 0.22);
    fg = darken(base, 0.45);
  } catch {
    bg = theme.palette.grey[100];
    fg = theme.palette.grey[700];
  }
  return {
    display: 'inline-block',
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.5,
    backgroundColor: bg,
    color: fg,
    border: eventtype === 'budget' ? `1px dashed ${alpha(fg, 0.55)}` : '1px solid transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
  };
});

// Mobile calendar-cell pill: cells are too narrow for descriptions, so each
// event renders as its amount on the category's color — same tint/darken rule
// (and dashed-planned border) as EventChip so the legend still applies.
const MobileAmountPill = styled('span', {
  shouldForwardProp: (prop) =>
    !['categorycolor', 'amount', 'eventtype'].includes(prop),
})(({ theme, categorycolor, amount, eventtype }) => {
  let bg, fg;
  try {
    const base = categorycolor || (amount > 0 ? theme.palette.success.main : theme.palette.error.main);
    bg = alpha(base, eventtype === 'budget' ? 0.12 : 0.22);
    fg = darken(base, 0.45);
  } catch {
    bg = theme.palette.grey[100];
    fg = theme.palette.grey[700];
  }
  return {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: '1px 2px',
    fontSize: '0.6rem',
    fontWeight: 600,
    lineHeight: 1.3,
    textAlign: 'center',
    backgroundColor: bg,
    color: fg,
    border: eventtype === 'budget' ? `1px dashed ${alpha(fg, 0.55)}` : '1px solid transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
});

// Small neutral status tag (Planned / Pending) for the List View, same
// clip-proof construction as ListPill.
const ListTag = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})(({ theme, tone }) => {
  const tones = {
    planned: {
      color: theme.palette.text.secondary,
      border: `1px dashed ${alpha(theme.palette.text.secondary, 0.45)}`,
      backgroundColor: 'transparent',
    },
    pending: {
      color: theme.palette.warning.dark,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
    },
  };
  const t = tones[tone] || tones.planned;
  return {
    display: 'inline-block',
    boxSizing: 'border-box',
    padding: '1px 8px',
    borderRadius: 999,
    fontSize: '0.7rem',
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    ...t,
  };
});

const TransactionCalendar = () => {
  const { transactions, categories, actions, budgets } = useData();
  const { toggles, toggleItem } = useCalendarToggles();
  const [weekStart] = usePreference('weekStart', 'monday');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTab, setSelectedTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [modalDay, setModalDay] = useState(null);

  // Extract toggle values from preferences
  const showIncome = toggles.showIncome;
  const showExpenses = toggles.showExpenses;
  const showTransfers = toggles.showTransfers;
  const showBudgets = toggles.showBudgets;
  const showTransactions = showIncome || showExpenses; // Show transactions if either income or expenses are enabled

  // Get all transfer category ids
  const transferCategoryIds = useMemo(() => categories.filter(c => c.isTransfer).map(c => c.id), [categories]);

  // Get category name
  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  // Week start is a user preference (Monday default)
  const weekStartsOn = weekStart === 'sunday' ? 0 : 1;

  // Get all days in the current month view (including padding days for full calendar grid)
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);

    // Get the start of the week containing the first day of the month
    const calendarStart = startOfWeek(monthStart, { weekStartsOn });
    // Get the end of the week containing the last day of the month
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate, weekStartsOn]);

  // Get all transactions, recurring actions and budgets for the current month.
  // Everything is generated unfiltered first (so the filter chips can show
  // counts), then the visibility toggles are applied at the end.
  const calendarData = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);

    // Get all transactions for the month
    const monthTransactions = transactions
      .filter(t => {
        const txnDate = new Date(t.date);
        return txnDate >= monthStart && txnDate <= monthEnd;
      })
      .map(t => ({
        id: t.id,
        date: new Date(t.date),
        amount: t.amount,
        description: t.description,
        category: t.category,
        type: 'transaction',
        isPending: t.isPending
      }));

    // Get recurring actions
    let recurringActions = actions
      .filter(a => a.isActive && !a.isCompleted && a.frequency !== 'once')
      .flatMap(action =>
        generateOccurrences(action, monthStart, monthEnd).map(date => ({
          id: `${action.id}_${date.toISOString()}`,
          date,
          amount: action.amount,
          description: action.name,
          category: action.categoryId,
          type: 'action',
          frequency: action.frequency,
          interval: action.interval
        }))
      );

    // Generate budget events (always, so the Budgets chip can show a count)
    let budgetEvents = [];
    if (budgets) {
      budgetEvents = budgets
        .filter(budget => budget.isActive)
        .flatMap(budget => {
          const dates = budget.isRecurring
            ? generateOccurrences(budget, monthStart, monthEnd)
            : generateOccurrences({ startDate: budget.startDate, frequency: 'once' }, monthStart, monthEnd);
          return dates.map(date => ({
            id: `budget_${budget.id}_${date.toISOString()}`,
            date,
            amount: (budget.type === 'Income' ? 1 : -1) * Math.abs(getBudgetAmountAt(budget, date)),
            description: budget.name || `${budget.type}: ${getCategoryName(budget.categoryId)}`,
            category: budget.categoryId,
            type: 'budget',
            budgetType: budget.type,
            frequency: budget.recurringPeriod
          }));
        });
    }

    // Per-layer counts for the filter chips (before visibility filtering)
    const counts = {
      income: monthTransactions.filter(t => t.amount > 0 && !transferCategoryIds.includes(t.category)).length,
      expenses: monthTransactions.filter(t => t.amount < 0 && !transferCategoryIds.includes(t.category)).length,
      transfers: monthTransactions.filter(t => transferCategoryIds.includes(t.category)).length,
      budgets: budgetEvents.length
    };

    // Apply visibility toggles
    const visibleTransactions = monthTransactions.filter(t => {
      if (!showTransfers && transferCategoryIds.includes(t.category)) return false;
      return (t.amount > 0 && showIncome) || (t.amount < 0 && showExpenses);
    });
    const visibleBudgetEvents = showBudgets ? budgetEvents : [];

    // Combine events
    const allEvents = [...visibleTransactions, ...recurringActions, ...visibleBudgetEvents];
    return { events: allEvents.sort((a, b) => a.date - b.date), counts };
  }, [transactions, actions, budgets, currentDate, showTransfers, showBudgets, showIncome, showExpenses, transferCategoryIds, getCategoryName]);

  const { events, counts } = calendarData;

  // Group events by LOCAL calendar date. Keys must match the day-cell keys
  // below, which come from local-midnight dates — toISOString() would shift
  // NZ (UTC+12) events with real UTC timestamps (e.g. pending transactions)
  // onto the next day's cell.
  const eventsByDate = useMemo(() => {
    return events.reduce((acc, event) => {
      const dateKey = format(event.date, 'yyyy-MM-dd');
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(event);
      return acc;
    }, {});
  }, [events]);

  // Ordered day groups for the List View. Events are already sorted by date,
  // so we just collect consecutive runs of the same day — this lets the list
  // show the date once as a header instead of repeating it on every card.
  const listGroups = useMemo(() => {
    const groups = [];
    let currentKey = null;
    events.forEach(event => {
      const key = format(event.date, 'yyyy-MM-dd');
      if (key !== currentKey) {
        groups.push({ key, date: event.date, items: [] });
        currentKey = key;
      }
      groups[groups.length - 1].items.push(event);
    });
    return groups;
  }, [events]);

  // Handle month navigation
  const handlePrevMonth = () => {
    setCurrentDate(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => addMonths(prev, 1));
  };

  // Get category color
  const getCategoryColor = (categoryId) => {
    if (!categoryId) return null;
    const category = categories.find(c => c.id === categoryId);
    return category ? category.color : null;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
    }).format(amount);
  };

  const handleDayClick = (dayEvents, day, net) => {
    if (dayEvents.length > 0) {
      setModalDay({ date: day, events: dayEvents, net });
    }
  };

  const getDaySummary = (events) => {
    let income = 0, expenses = 0;
    events.forEach(e => {
      if (e.amount > 0) income += e.amount;
      else expenses += Math.abs(e.amount);
    });
    const net = income - expenses;
    return { income, expenses, net };
  };

  // Responsive layout toggle (Tabs desktop, Select mobile) similar to Budgets header
  const viewOptions = [
    { value: 0, label: 'Calendar' },
    { value: 1, label: 'List' }
  ];
  // Reuse existing isMobile defined earlier in component (avoid redeclaration)

  // Per-day visible events and nets, chunked into weeks for the grid
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const dayData = days.map(day => {
    const dateKey = format(day, 'yyyy-MM-dd');
    let dayEvents = eventsByDate[dateKey] || [];
    const isPast = day < todayStart;
    if (isPast && showTransactions) {
      dayEvents = dayEvents.filter(e => e.type === 'transaction');
    }
    const { net } = getDaySummary(dayEvents);
    return { day, dateKey, dayEvents, net };
  });
  const weeks = [];
  for (let i = 0; i < dayData.length; i += 7) weeks.push(dayData.slice(i, i + 7));

  const weekdayLabels = weekStartsOn === 1
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const showWeekTotals = !isMobile;

  const filterDefs = [
    { key: 'showIncome', label: 'Income', value: showIncome, count: counts.income },
    { key: 'showExpenses', label: 'Expenses', value: showExpenses, count: counts.expenses },
    { key: 'showTransfers', label: 'Transfers', value: showTransfers, count: counts.transfers, disabled: !showTransactions },
    { key: 'showBudgets', label: 'Budgets', value: showBudgets, count: counts.budgets }
  ];
  const allLayersOff = filterDefs.every(f => !f.value);

  return (
    <Box sx={{ width: '100%' }}>
      <SharedPageHeader
        title="Calendar"
        icon={<CalendarMonthIcon fontSize="large" />}
        actions={
          <SegmentedControl
            options={viewOptions}
            value={selectedTab}
            onChange={setSelectedTab}
          />
        }
      />

      {/* Month nav + layer filters share one compact row */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, my: 1.5, px: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={handlePrevMonth} size="small" aria-label="Previous month" sx={{ color: 'text.secondary', p: 0.5, '&:hover': { bgcolor: 'grey.100' } }}>
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary', minWidth: 110, textAlign: 'center', whiteSpace: 'nowrap' }}>
            {format(currentDate, 'MMMM yyyy')}
          </Typography>
          <IconButton onClick={handleNextMonth} size="small" aria-label="Next month" sx={{ color: 'text.secondary', p: 0.5, '&:hover': { bgcolor: 'grey.100' } }}>
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
          <Button
            size="small"
            onClick={() => setCurrentDate(new Date())}
            disabled={isSameMonth(currentDate, new Date())}
            sx={{ textTransform: 'none', fontSize: '0.8125rem', minWidth: 0, px: 1 }}
          >
            Today
          </Button>
        </Box>

        {/* Layer filters — labelled gray-track pill toggles (multi-select),
            matching the Options control on the Analysis page */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.25, rowGap: 1 }}>
          <Typography variant="body3" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Options
          </Typography>
          <Box sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 6, p: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
            {filterDefs.map(f => (
              <ButtonBase
                key={f.key}
                disabled={f.disabled}
                onClick={() => toggleItem(f.key, !f.value)}
                aria-pressed={f.value}
                sx={{
                  py: 0.75,
                  px: 1.25,
                  borderRadius: 5,
                  fontFamily: 'inherit',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  color: f.value ? 'primary.main' : 'text.secondary',
                  bgcolor: f.value ? 'white' : 'transparent',
                  transition: 'background-color 0.15s, color 0.15s',
                  '&.Mui-disabled': { opacity: 0.4 }
                }}
              >
                {f.label}
              </ButtonBase>
            ))}
          </Box>
        </Box>
      </Box>

      {allLayersOff && events.length === 0 && (
        <InfoBanner sx={{ mb: 1 }}>
          All layers are hidden. Turn a filter back on above to see your activity.
        </InfoBanner>
      )}

      <CalendarContainer>

        {selectedTab === 0 ? (
          <CalendarGrid sx={{
            gridTemplateColumns: showWeekTotals ? 'repeat(7, 1fr) minmax(56px, 0.4fr)' : 'repeat(7, 1fr)',
            gridTemplateRows: `auto repeat(${weeks.length}, minmax(${isMobile ? 76 : 96}px, 1fr))`
          }}>
            {/* Weekday headers */}
            {weekdayLabels.map(label => (
              <Typography
                key={label}
                variant="subtitle2"
                align="center"
                sx={{
                  fontWeight: 600,
                  mb: 0.3,
                  fontSize: 'clamp(0.6rem, 1vw, 0.75rem)',
                  color: 'text.secondary'
                }}
              >
                {label}
              </Typography>
            ))}
            {showWeekTotals && (
              <Typography
                variant="subtitle2"
                align="center"
                sx={{
                  fontWeight: 600,
                  mb: 0.3,
                  fontSize: 'clamp(0.6rem, 1vw, 0.75rem)',
                  color: 'text.secondary'
                }}
              >
                Net
              </Typography>
            )}

            {/* Calendar days, one row per week with a week-net cell at the end */}
            {weeks.map(week => {
              const weekNet = week.reduce((sum, d) => sum + d.net, 0);
              const weekHasEvents = week.some(d => d.dayEvents.length > 0);
              return (
                <React.Fragment key={week[0].dateKey}>
                  {week.map(({ day, dateKey, dayEvents, net }) => {
                    const dayIsToday = isToday(day);
                    const netColor = net > 0
                      ? theme.palette.success.main
                      : net < 0
                        ? theme.palette.error.main
                        : theme.palette.text.disabled;
                    const eventTooltip = (event) =>
                      `${event.description} · ${formatCurrency(event.amount)} · ${getCategoryName(event.category)}${event.type === 'budget' ? ' · planned' : event.type === 'action' ? ' · scheduled' : ''}`;
                    const dayContent = !isMobile ? (
                      <>
                        <Box sx={{
                          position: 'absolute',
                          top: theme.spacing(0.2),
                          left: theme.spacing(0.2),
                          right: theme.spacing(0.2),
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          width: 'calc(100% - 2 * ' + theme.spacing(0.2) + ')',
                          zIndex: 2,
                          fontSize: 'clamp(0.5rem, 0.8vw, 0.7rem)',
                          fontWeight: 600,
                          lineHeight: 1,
                          fontFamily: 'inherit',
                        }}>
                          {/* Quiet days carry no amount — an empty span keeps the day number right-aligned */}
                          {dayEvents.length > 0 ? (
                            <Tooltip title="Net of items shown" placement="top">
                              <span style={{
                                color: netColor,
                                display: 'inline-block',
                                position: 'relative',
                                top: '0.5px',
                                marginRight: '2px',
                                fontSize: 'clamp(0.45rem, 0.7vw, 0.6rem)',
                              }}>{formatCurrency(net)}</span>
                            </Tooltip>
                          ) : <span />}
                          {dayIsToday ? (
                            <Box component="span" sx={{
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              borderRadius: '50%',
                              width: 16,
                              height: 18,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 'clamp(0.5rem, 0.7vw, 0.65rem)',
                              fontWeight: 700,
                              lineHeight: 1,
                            }}>{format(day, 'd')}</Box>
                          ) : (
                            <span style={{
                              color: 'inherit',
                              display: 'inline-block',
                              position: 'relative',
                              top: '0.5px',
                              marginLeft: '2px',
                              fontSize: 'clamp(0.55rem, 0.8vw, 0.75rem)',
                              fontWeight: 600,
                            }}>{format(day, 'd')}</span>
                          )}
                        </Box>
                        <Box sx={{ mt: 1.8, pt: 0.3, pl: 0.1, pr: 0.1, pb: 0.1, overflow: 'hidden', flex: 1, minHeight: 0 }}>
                          {dayEvents.slice(0, 3).map(event => (
                            <Tooltip
                              key={event.id}
                              title={eventTooltip(event)}
                              placement="top"
                            >
                              <EventChip
                                label={event.description}
                                size="small"
                                categorycolor={getCategoryColor(event.category)}
                                amount={event.amount}
                                eventtype={event.type}
                                sx={{ mb: 0.1, width: '100%', maxWidth: '100%' }}
                              />
                            </Tooltip>
                          ))}
                          {dayEvents.length > 3 && (
                            <Button
                              size="small"
                              sx={{
                                mt: 0.1,
                                fontSize: 'clamp(0.5rem, 0.8vw, 0.65rem)',
                                minHeight: 'clamp(11px, 1.2vw, 15px)',
                                minWidth: 0,
                                p: 0.2,
                                width: '100%',
                                textTransform: 'none'
                              }}
                              onClick={(e) => { e.stopPropagation(); handleDayClick(dayEvents, day, net); }}
                            >
                              +{dayEvents.length - 3} more
                            </Button>
                          )}
                        </Box>
                      </>
                    ) : (
                      <>
                        {/* Today's cell already carries a border + tint, so the
                            number only needs color/weight — a filled badge at
                            this size renders as a cramped blob. */}
                        <DayNumber sx={dayIsToday ? {
                          color: 'primary.main',
                          fontWeight: 800,
                        } : undefined}>{format(day, 'd')}</DayNumber>
                        {dayEvents.length > 0 && (
                          <Box sx={{
                            mt: 2.2,
                            px: 0.15,
                            pb: 0.15,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                          }}>
                            <Box sx={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              color: netColor,
                              textAlign: 'center',
                              lineHeight: 1.2,
                            }}>
                              {formatCurrency(net)}
                            </Box>
                            {dayEvents.slice(0, 3).map(event => (
                              <MobileAmountPill
                                key={event.id}
                                categorycolor={getCategoryColor(event.category)}
                                amount={event.amount}
                                eventtype={event.type}
                              >
                                {formatCurrency(event.amount)}
                              </MobileAmountPill>
                            ))}
                            {dayEvents.length > 3 && (
                              <Box sx={{
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                color: 'text.secondary',
                                textAlign: 'center',
                                lineHeight: 1.2,
                              }}>
                                +{dayEvents.length - 3} more
                              </Box>
                            )}
                          </Box>
                        )}
                      </>
                    );
                    return (
                      <CalendarDay
                        key={dateKey}
                        isCurrentMonth={isSameMonth(day, currentDate)}
                        isToday={dayIsToday}
                        hasEvents={dayEvents.length > 0}
                        onClick={dayEvents.length > 0 ? () => handleDayClick(dayEvents, day, net) : undefined}
                      >
                        {dayContent}
                      </CalendarDay>
                    );
                  })}
                  {showWeekTotals && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}>
                      <Typography sx={{
                        fontSize: 'clamp(0.55rem, 0.8vw, 0.75rem)',
                        fontWeight: 700,
                        color: !weekHasEvents || weekNet === 0
                          ? 'text.disabled'
                          : weekNet > 0 ? 'success.main' : 'error.main',
                      }}>
                        {weekHasEvents ? formatCurrency(weekNet) : '—'}
                      </Typography>
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </CalendarGrid>
        ) : (
          <Box>
            {listGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Nothing to show for {format(currentDate, 'MMMM yyyy')}.
              </Typography>
            ) : (
              listGroups.map(group => {
                const dayNet = group.items.reduce((sum, e) => sum + e.amount, 0);
                return (
                  <Box key={group.key} sx={{ mb: 1.25 }}>
                    {/* One date header per day instead of repeating the full
                        date on every card. Sticky so the day stays in view. */}
                    <Box sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 1,
                      bgcolor: 'background.paper',
                      py: 0.5,
                      px: 0.5,
                      mb: 0.4,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: isToday(group.date) ? 'primary.main' : 'text.secondary' }}>
                        {format(group.date, 'EEEE, MMMM d')}{isToday(group.date) ? ' · Today' : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: dayNet > 0 ? 'success.main' : dayNet < 0 ? 'error.main' : 'text.disabled' }}>
                        {formatCurrency(dayNet)}
                      </Typography>
                    </Box>
                    {group.items.map(event => (
                      <Card key={event.id} sx={{ mb: 0.4, boxShadow: 'none', borderRadius: theme.shape.borderRadius }}>
                        <CardContent sx={{ py: 0.6, px: 1, '&:last-child': { pb: 0.6 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.875rem' }} noWrap>
                                {event.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4, alignItems: 'center', flexWrap: 'wrap' }}>
                                <ListPill
                                  categorycolor={getCategoryColor(event.category)}
                                  amount={event.amount}
                                  eventtype={event.type}
                                >
                                  {getCategoryName(event.category)}
                                </ListPill>
                                {event.type === 'budget' && <ListTag tone="planned">Planned</ListTag>}
                                {event.isPending && <ListTag tone="pending">Pending</ListTag>}
                              </Box>
                            </Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              color={event.amount > 0 ? 'success.main' : 'error.main'}
                              sx={{ ml: 1, whiteSpace: 'nowrap', fontSize: '0.875rem' }}
                            >
                              {formatCurrency(event.amount)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </CalendarContainer>
      {modalDay && (
        <Dialog
          open={!!modalDay}
          onClose={() => setModalDay(null)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { overflow: 'hidden' } }}
        >
          <DialogHeader
            title={format(modalDay.date, 'EEEE')}
            subtitle={format(modalDay.date, 'MMMM d, yyyy')}
            onClose={() => setModalDay(null)}
          />

          {/* Summary strip: item count + net for the day */}
          <DialogSummaryBar
            left={
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {modalDay.events.length} {modalDay.events.length === 1 ? 'item' : 'items'}
              </Typography>
            }
            right={
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  Net
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} sx={{
                  color: modalDay.net > 0 ? 'success.main' : modalDay.net < 0 ? 'error.main' : 'text.secondary',
                }}>
                  {formatCurrency(modalDay.net)}
                </Typography>
              </Box>
            }
          />

          {/* Divider-separated rows on the paper background, matching the
              transaction-list dialog and the List View — the category is
              carried by its chip, not by per-row accent chrome. */}
          <DialogContent sx={{ p: 0 }}>
            {modalDay.events.map((event, index) => (
              <Box key={event.id} sx={{
                px: 2.5,
                py: 1.25,
                borderBottom: index < modalDay.events.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.25 }}>
                  <Tooltip title={event.description} placement="top-start">
                    <Typography noWrap variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', minWidth: 0, flex: 1 }}>
                      {event.description}
                    </Typography>
                  </Tooltip>
                  <Typography variant="subtitle2" fontWeight={800} sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    fontSize: '0.85rem',
                    color: event.amount > 0 ? 'success.main' : 'error.main',
                  }}>
                    {formatCurrency(event.amount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {event.type === 'transaction' && (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {format(event.date, 'h:mm a')}
                    </Typography>
                  )}
                  <CategoryChip
                    color={getCategoryColor(event.category)}
                    label={getCategoryName(event.category)}
                    size="small"
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                  {event.type === 'budget' && (
                    <Chip label="Planned" size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 20, borderStyle: 'dashed', color: 'text.secondary' }} />
                  )}
                  {event.type === 'action' && event.frequency && (
                    <Chip label={cadenceLabel(event.frequency, event.interval)} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 20, color: 'text.secondary' }} />
                  )}
                  {event.isPending && (
                    <Chip label="Pending" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.75rem', height: 20 }} />
                  )}
                </Box>
              </Box>
            ))}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default TransactionCalendar;
